// One tier of the chart furniture as an engine layer (R548).
//
// The client builds two of these: `chartsea`, which draws under the beast
// and every actionable object, and `chartscreen`, which draws over the
// fleets and under the ambience (§3). Each one holds SIX batches — quads,
// segments and circles, twice — because a tier carries two packs:
//
//   - the STATIC pack repacks only when a piece names a reason, and costs
//     three flushes a frame after that. The place names of a world, the
//     isobaths of a trench and the banks of a bay are written one time;
//   - the LIVE pack begins every frame and every live piece writes into it.
//     A hull's track, a bearing line and the compass needle move, so they
//     are packed where the frame is.
//
// Splitting the two is the whole point of the layer. One pack would repack
// a world's worth of place names sixty times a second to move one needle.
//
// The layer owns no rule of its own: the common gate is the controller's
// (`furniture.ts`), and this asks for it through `gate`. What the layer DOES
// own is the edge: a piece that leaves the gate, or joins it, repacks the
// static set on that frame and on no other.

import type { CameraUniforms, DrawCtx, FrameInfo, Layer } from '../layer';
import type { BatchSource } from '../layers/batches';
import type { ChartContext, ChartSinks, Piece, PickHit } from './context';
import type { SmallMark } from '../smallmarks';

/**
 * The instances one pack is sized for. The batches grow on demand, so this
 * is the first allocation and never a ceiling.
 */
const QUAD_CAP = 512;
const LINE_CAP = 512;
const CIRCLE_CAP = 128;

/** What the layer asks of the controller. */
export interface ChartLayerDeps {
  /** The one context, rewritten in place before the layers update. */
  ctx(): ChartContext;
  /** The common gate: may this piece draw on this frame? */
  gate(p: Piece): boolean;
  /**
   * The controller's own tick, at the top of the update.
   *
   * A replay film takes the frame away from the scene (R531), and the
   * terrain furniture stays on the glass under a film — so the layer calls
   * the tick as well as the scene does. The tick is idempotent inside one
   * frame, so the second call of a frame changes nothing.
   */
  tick(frame: FrameInfo): void;
  /** A piece threw in a pack or a pass: the controller drops it (R548). */
  fail(p: Piece, phase: string, err: unknown): void;
}

/** The three batches of one pack. */
function newSinks(src: BatchSource): ChartSinks {
  return {
    quads: src.newQuads(QUAD_CAP),
    segments: src.newSegments(LINE_CAP),
    circles: src.newCircles(CIRCLE_CAP),
  };
}

/** Empties one pack. */
function begin(s: ChartSinks): void {
  s.quads.begin();
  s.segments.begin();
  s.circles.begin();
}

/** Draws one pack: the circles, the segments, then the quads over both. */
function flush(s: ChartSinks, cam: CameraUniforms): void {
  s.circles.flush(cam);
  s.segments.flush(cam);
  s.quads.flush(cam);
}

/** Dims one pack: the fade as one uniform on each of its three batches. */
function dim(s: ChartSinks, a: number): void {
  s.quads.globalAlpha = a;
  s.segments.globalAlpha = a;
  s.circles.globalAlpha = a;
}

export class ChartLayer implements Layer {
  /** The static pack: three sinks that hold their instances between frames. */
  private readonly staticSinks: ChartSinks;
  /** The live pack: three sinks that begin again on every frame. */
  private readonly liveSinks: ChartSinks;

  private readonly statics: Piece[] = [];
  private readonly lives: Piece[] = [];
  /** Whether each static piece passed the gate at the last pack. */
  private readonly wasOn = new Map<Piece, boolean>();

  private shown = true;
  private dirty = true;
  private fade = 1;

  /** How many times the static pack was written. The tests read it. */
  packs = 0;

  constructor(readonly name: string, src: BatchSource, private readonly d: ChartLayerDeps) {
    this.staticSinks = newSinks(src);
    this.liveSinks = newSinks(src);
  }

  /** Takes one piece. The order is the registry's, and it is the draw order. */
  add(p: Piece): void {
    if (p.pack === 'static') this.statics.push(p);
    else this.lives.push(p);
  }

  /** The static pack must be written again on the next update. */
  markDirty(): void { this.dirty = true; }

  /**
   * The fade of a reading change, as ONE uniform on each of the six batches
   * (R548). A layer that dimmed by scaling the alpha of every instance would
   * have to repack the whole world to fade it.
   */
  setAlpha(a: number): void {
    this.fade = a;
    dim(this.staticSinks, a);
    dim(this.liveSinks, a);
  }

  /** What the fade stands at now. */
  get alpha(): number { return this.fade; }

  /**
   * Repacks the static set when it is stale, and packs the live set.
   *
   * An invisible layer is never updated by the loop (§10.3), so `off` costs
   * nothing at all.
   */
  update(frame: FrameInfo): void {
    this.d.tick(frame);
    const ctx = this.d.ctx();
    // The screen band of the words: four numbers a frame, one uniform on
    // each quad batch, and no repack (R1796).
    this.staticSinks.quads.stepScale.set(ctx.stepScale);
    this.liveSinks.quads.stepScale.set(ctx.stepScale);
    // The gate edge. A piece that just left the gate must be taken OUT of
    // the static pack, and a piece that just joined it must be written in,
    // so the edge is a repack whichever way it went.
    let dirty = this.dirty;
    for (const p of this.statics) {
      const on = this.d.gate(p);
      if (on !== this.wasOn.get(p)) {
        this.wasOn.set(p, on);
        dirty = true;
      }
    }
    if (dirty) {
      begin(this.staticSinks);
      for (const p of this.statics) {
        if (!this.wasOn.get(p)) continue;
        try { p.emit(this.staticSinks, ctx, frame); } catch (err) { this.d.fail(p, 'emit', err); }
      }
      this.dirty = false;
      this.packs++;
    }
    // The live pack. It begins on every frame, gate or no gate: a batch that
    // kept the last frame's instances would draw a piece the gate has
    // already taken away.
    begin(this.liveSinks);
    for (const p of this.lives) {
      if (!this.d.gate(p)) continue;
      // A throw names the step it came from, so a report can tell an
      // update from a pack.
      let phase = 'update';
      try {
        p.update?.(ctx, frame);
        phase = 'emit';
        p.emit(this.liveSinks, ctx, frame);
      } catch (err) {
        this.d.fail(p, phase, err);
      }
    }
  }

  /**
   * Six flushes: the static pack first, and the live pack over it.
   *
   * A piece that carries a pass of its own then draws it, over the batches
   * of the same tier and in the registry's order. Only the sonar sweep does
   * (card 59), so the loop costs one test a piece on every other frame.
   */
  draw(ctx: DrawCtx): void {
    flush(this.staticSinks, ctx.cam);
    flush(this.liveSinks, ctx.cam);
    // The two lists are walked apart, so a draw path allocates no joined
    // list (§10.3).
    this.drawExtras(this.statics, ctx);
    this.drawExtras(this.lives, ctx);
  }

  /** The own pass of every piece of one list that carries one. */
  private drawExtras(list: readonly Piece[], ctx: DrawCtx): void {
    for (const p of list) {
      if (!p.drawExtra || !this.d.gate(p)) continue;
      try { p.drawExtra(ctx, this.fade); } catch (err) { this.d.fail(p, 'draw', err); }
    }
  }

  /**
   * The nearest mark of this tier under a world point, or null.
   *
   * The pieces are asked in REVERSE draw order, so the piece drawn last —
   * the one the player sees on top — answers a tie.
   */
  pickAt(wx: number, wy: number, tolPx: number): PickHit | null {
    let best: PickHit | null = null;
    let bestD = Infinity;
    // One index over both lists in draw order, the live pieces last, so a
    // pick allocates no joined list.
    const nStatic = this.statics.length;
    for (let i = nStatic + this.lives.length - 1; i >= 0; i--) {
      const p = i >= nStatic ? this.lives[i - nStatic] : this.statics[i];
      if (!p.pick || !this.d.gate(p)) continue;
      const hit = p.pick(wx, wy, tolPx);
      if (!hit) continue;
      const d = Math.hypot(hit.x - wx, hit.y - wy);
      if (d < bestD) { best = hit; bestD = d; }
    }
    return best;
  }

  /** Offers the small marks of this tier under a world point (R2851):
   *  every piece that passes the gate writes its nearest one into `out`. */
  markPickAt(wx: number, wy: number, margin: number, out: SmallMark): void {
    const nStatic = this.statics.length;
    for (let i = nStatic + this.lives.length - 1; i >= 0; i--) {
      const p = i >= nStatic ? this.lives[i - nStatic] : this.statics[i];
      if (p.markPick && this.d.gate(p)) p.markPick(wx, wy, margin, out);
    }
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  dispose(): void {
    for (const p of this.statics) p.dispose?.();
    for (const p of this.lives) p.dispose?.();
    this.statics.length = 0;
    this.lives.length = 0;
    this.wasOn.clear();
  }
}
